import { FileClock, FilePlus2, FileX2, Upload, BookText, Info } from 'lucide-react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';

import { NavGroup, NavItem } from './Navigation';

enum Tabs {
  CaseMetadata = 'Case Metadata',
  CaseDetails = 'Case Details',
  Documents = 'Add Documents',
  Runs = 'Runs',
  Uploads = 'Uploads',
  DeleteDocs = 'Delete Docs',
}

interface SettingsLayoutProps {
  children: React.ReactNode;
}

export const SettingsLayout = ({ children }: SettingsLayoutProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseId = searchParams.get('caseId');

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/metadata')) return Tabs.CaseMetadata;
    if (path.includes('/details')) return Tabs.CaseDetails;
    if (path.includes('/documents')) return Tabs.Documents;
    if (path.includes('/runs')) return Tabs.Runs;
    if (path.includes('/uploads')) return Tabs.Uploads;
    if (path.includes('/delete')) return Tabs.DeleteDocs;
    return Tabs.CaseMetadata;
  };

  const activeTab = getActiveTab();

  const navigateToTab = (tab: string) => {
    const basePath = `/app/chronos/case-editor/settings`;
    navigate(`${basePath}/${tab}?caseId=${caseId}`);
  };

  return (
    <div className="flex h-full w-full border-b">
      <div className="transition-width relative mr-2 flex h-full w-42 flex-col border-r border-gray-200/80 bg-gray-50 py-1 duration-500 ease-in-out">
        <div className="flex h-full flex-col overflow-y-auto px-1 py-2">
          <div className="flex-1">
            <NavGroup title="Case Information">
              <NavItem
                title="Case Metadata"
                icon={<Info className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.CaseMetadata}
                onClick={() => navigateToTab('metadata')}
              />
              <NavItem
                title="Case Details"
                icon={<BookText className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.CaseDetails}
                onClick={() => navigateToTab('details')}
              />
              <NavItem
                title="Add Documents"
                icon={<FilePlus2 className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.Documents}
                onClick={() => navigateToTab('documents')}
              />
            </NavGroup>

            <NavGroup title="Activity">
              <NavItem
                title="Runs"
                icon={<FileClock className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.Runs}
                onClick={() => navigateToTab('runs')}
              />
              <NavItem
                title="Uploads"
                icon={<Upload className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.Uploads}
                onClick={() => navigateToTab('uploads')}
              />
            </NavGroup>
          </div>

          <div className="mt-auto">
            <NavGroup title="Danger Zone">
              <NavItem
                title="Delete Docs"
                icon={<FileX2 className="h-3 w-3 shrink-0 text-gray-700" />}
                active={activeTab === Tabs.DeleteDocs}
                onClick={() => navigateToTab('delete')}
              />
            </NavGroup>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="h-full w-full overflow-auto px-4 py-4">{children}</div>
    </div>
  );
};
